import { lazy } from "react";

import { Navigate, useLocation } from "react-router-dom";

import { useSessionStore } from "@/entities/session";
import Layout from "@/shared/ui/layout/Layout";
import { ThemeLayout } from "@/shared/ui/layout/ThemeLayout.tsx";
import { Roles } from "@/entities/user";

interface ProtectedRouteProps {
  roles: string[];
  children: JSX.Element;
}

export const ProtectedRoute = () => {
  const { user, isAuthenticated } = useSessionStore();

  if (!user || !isAuthenticated) return <Navigate to="/login" />;

  return <Layout />;
};

export const RoleRoute = ({ roles, children }: ProtectedRouteProps) => {
  const { user, isAuthenticated } = useSessionStore();

  if (!user || !isAuthenticated) return <Navigate to="/login" />;
  if (roles.includes(user.role)) return <>{children}</>;

  return <Navigate to="/" />;
};

export const routes = [
  {
    Component: ProtectedRoute,
    children: [
      {
        index: true,
        path: "/",
        Component: lazy(() =>
          import("./restricted/shared/main-page/MainPage").then((module) => ({
            default: () => (
              <RoleRoute
                roles={[
                  Roles.Head,
                  Roles.Worker,
                  Roles.TeamLead,
                  Roles.Manager,
                  Roles.MasterTeamLead,
                ]}
              >
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/clans",
        Component: lazy(() =>
          import("./restricted/head/clans-page/ClansPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/clans/my",
        Component: lazy(() =>
          import("./restricted/shared/clans-page/MyClanPage").then(
            (module) => ({
              default: () => (
                <RoleRoute roles={[Roles.Worker]}>{module.default()}</RoleRoute>
              ),
            }),
          ),
        ),
      },
      {
        path: "/clans/my/all-leads",
        Component: lazy(() =>
          import("./restricted/shared/clans-page/MyClanAllLeadsPage").then(
            (module) => ({
              default: () => (
                <RoleRoute roles={[Roles.Worker]}>{module.default()}</RoleRoute>
              ),
            }),
          ),
        ),
      },
      {
        path: "/clans",
        Component: lazy(() =>
          import("./restricted/shared/clans-page/ClansPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Worker]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/manager/leads",
        Component: lazy(() =>
          import("./restricted/manager/leads-page/LeadsPage").then(
            (module) => ({
              default: () => (
                <RoleRoute
                  roles={[Roles.Manager, Roles.TeamLead, Roles.MasterTeamLead]}
                >
                  {module.default()}
                </RoleRoute>
              ),
            }),
          ),
        ),
      },
      {
        path: "/worker/links",
        Component: lazy(() =>
          import("./restricted/shared/links-page/LinksPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Worker, Roles.Head]}>
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/workers",
        Component: lazy(() =>
          import("./restricted/head/WorkersPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/worker/leads",
        Component: lazy(() =>
          import("./restricted/shared/LeadsPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Worker, Roles.Head]}>
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/analytics",
        Component: lazy(() =>
          import("./restricted/shared/analytics-page/AnalyticsPage").then(
            (module) => ({
              default: () => (
                <RoleRoute roles={[Roles.Worker, Roles.Head]}>
                  {module.default()}
                </RoleRoute>
              ),
            }),
          ),
        ),
      },
      {
        path: "/teamlead/access",
        Component: lazy(() =>
          import("./restricted/teamlead/AccessPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.TeamLead, Roles.MasterTeamLead]}>
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/teamlead/managers",
        Component: lazy(() =>
          import("./restricted/teamlead/ManagersPage").then((module) => ({
            default: () => (
              <RoleRoute
                roles={[Roles.TeamLead, Roles.MasterTeamLead, Roles.Head]}
              >
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/all-leads",
        Component: lazy(() =>
          import("./restricted/head/AllLeadsPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/clan-links",
        Component: lazy(() =>
          import("./restricted/head/clan-links-page/ClanLinksPage").then(
            (module) => ({
              default: () => (
                <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
              ),
            }),
          ),
        ),
      },
      {
        path: "/head/profits",
        Component: lazy(() =>
          import("./restricted/head/ProfitsPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/access",
        Component: lazy(() =>
          import("./restricted/head/AccessPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/workers",
        Component: lazy(() =>
          import("./restricted/head/WorkersPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/payouts",
        Component: lazy(() =>
          import("./restricted/head/PayoutsPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/proxies",
        Component: lazy(() =>
          import("./restricted/head/ProxiesPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/head/giveaways",
        Component: lazy(() =>
          import("./restricted/head/GiveawaysPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head]}>{module.default()}</RoleRoute>
            ),
          })),
        ),
      },
      {
        path: "/bots",
        Component: lazy(() =>
          import("./restricted/bots/BotsPage").then((module) => ({
            default: () => (
              <RoleRoute roles={[Roles.Head, Roles.Worker]}>
                {module.default()}
              </RoleRoute>
            ),
          })),
        ),
      },
    ],
  },
  {
    path: "/login",
    Component: lazy(() =>
      import("./public/login-page/LoginPage").then((module) => ({
        default: () => <ThemeLayout>{module.default()}</ThemeLayout>,
      })),
    ),
    isPublic: true,
  },
  {
    path: "/registration",
    Component: lazy(() =>
      import("./public/RegisterPage").then((module) => ({
        default: () => <ThemeLayout>{module.default()}</ThemeLayout>,
      })),
    ),
    isPublic: true,
  },
  {
    path: "/register",
    Component: () => {
      const location = useLocation();
      return <Navigate to={`/registration${location.search}`} replace />;
    },
  },
  {
    path: "*",
    Component: lazy(() =>
      import("./public/NotFoundPage").then((module) => ({
        default: () => <ThemeLayout>{module.default()}</ThemeLayout>,
      })),
    ),
    isPublic: true,
  },
];
